import { WALLETS_STORE_GETTERS } from '~/app/modules/processing';
import {
  BIRTH_PLACE_TEXT_MAX_LENGTH,
  EMAIL_CODE_LENGTH,
  IBAN_ACCOUNT_VALUE_LENGTH,
  MOBILE_PHONE_LENGTH,
  PASSPORT_ID_MAX_LENGTH,
  PASSPORT_ID_MIN_LENGTH,
  SMS_CODE_LENGTH,
} from '~/app/shared/utils/consts';
import {
  ALREADY_SENT_IN_LAST_MINUTE,
  BIOMETRY_EXPIRED,
  DAY_LIMIT_GET_SMS,
  EXPIRED_DATE_FIELD_VALIDATION_FAILED,
  INCORRECT_NEW_PASSWORD,
  INCORRECT_OLD_PASSWORD,
  INVALID_BANK,
  INVALID_PHONE_FORMAT,
  INVALID_RECAPTCHA_TOKEN,
  NOT_SAME_AS_CLIENT_PHONE,
  NOT_SAME_PHONE,
  NOT_UNIQUE,
  NOT_VALID_DOCUMENT_NUMBER,
  NOT_VALID_IBAN_VALUE,
  SMS_CODE_VALIDATION_ERROR,
  WALLET_IS_NOT_IDENTIFIED,
} from '~/app/shared/utils/errors';

const currentDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return { day, month, year };
};

export default ({ app }) => {
  function getMessages(key) {
    const { availableLocales, messages } = app.$lang;

    return availableLocales.reduce((errorMessages, locale) => {
      const message = messages[locale].rules;
      errorMessages[locale] = message ? message[key] : '';
      return errorMessages;
    }, {});
  }

  app.$validator.define({
    required: {
      rule({ value }) {
        if (typeof value === 'string') {
          return !value.trim();
        }

        return !value;
      },
      get messages() {
        return getMessages('required');
      },
      selector: 'error-required',
    },

    email: {
      rule({ value }) {
        /* eslint-disable-next-line */
        const REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return !REGEX.test(value);
      },
      get messages() {
        return getMessages('email');
      },
      selector: 'error-email',
    },

    iban: {
      code: INVALID_BANK,
      rule({ complete }) {
        return !complete;
      },
      get messages() {
        return getMessages('iban');
      },
      selector: 'error-iban',
    },

    notValidIbanValue: {
      code: NOT_VALID_IBAN_VALUE,
      get messages() {
        return getMessages('not-valid-iban');
      },
      selector: 'error-not-valid-iban',
    },

    bankAccountLength: {
      rule({ value = '' }) {
        return value.length !== IBAN_ACCOUNT_VALUE_LENGTH;
      },
      get messages() {
        return getMessages('bank-account-length');
      },
      selector: 'error-bank-account-length',
    },

    notValidDocumentNumber: {
      code: NOT_VALID_DOCUMENT_NUMBER,
      get messages() {
        return getMessages('not-valid-document-number');
      },
      selector: 'error-not-valid-document-number',
    },

    passportIDLength: {
      rule({ value = '' }) {
        return value.length !== PASSPORT_ID_MIN_LENGTH && value.length !== PASSPORT_ID_MAX_LENGTH;
      },
      get messages() {
        return getMessages('passport-id-length');
      },
      selector: 'error-passport-id-length',
    },

    phoneLength: {
      rule({ value }) {
        return value.length !== MOBILE_PHONE_LENGTH;
      },
      get messages() {
        return getMessages('phone-length');
      },
      selector: 'error-phone-length',
    },

    codeLength: {
      rule({ value = '' }) {
        return value.length !== SMS_CODE_LENGTH;
      },
      get messages() {
        return getMessages('code-length');
      },
      selector: 'error-code-length',
    },

    emailCodeLength: {
      rule({ value = '' }) {
        return value.length !== EMAIL_CODE_LENGTH;
      },
      get messages() {
        return getMessages('email-code-length');
      },
      selector: 'error-email-code-length',
    },

    incomeLength: {
      rule({ value }) {
        return value.length < 3;
      },
      get messages() {
        return getMessages('income-length');
      },
      selector: 'error-income-length',
    },

    notSamePhone: {
      code: NOT_SAME_PHONE,
      get messages() {
        return getMessages('not-same-phone');
      },
      selector: 'error-not-same-phone',
    },

    expiredBiometry: {
      code: BIOMETRY_EXPIRED,
      rule({ valid }) {
        return !valid;
      },
      get messages() {
        return getMessages('expired-biometry');
      },
      selector: 'error-expired-biometry',
    },

    checkPassword: {
      rule({ secondValue, value }) {
        return JSON.stringify(value) !== JSON.stringify(secondValue);
      },
      get messages() {
        return getMessages('check-password');
      },
      selector: 'error-check-password',
    },

    incorrectOldPassword: {
      code: INCORRECT_OLD_PASSWORD,
      get messages() {
        return getMessages('incorrect-old-password');
      },
      selector: 'error-incorrect-old-password',
    },

    incorrectNewPassword: {
      code: INCORRECT_NEW_PASSWORD,
      get messages() {
        return getMessages('incorrect-new-password');
      },
      selector: 'error-incorrect-new-password',
    },

    smsCodeNotPassed: {
      rule({ errorStatus }) {
        return errorStatus === SMS_CODE_VALIDATION_ERROR;
      },
      get messages() {
        return getMessages('sms-code-not-passed');
      },
      selector: 'error-sms-code-not-passed',
    },

    sentInLastMinute: {
      code: ALREADY_SENT_IN_LAST_MINUTE,
      get messages() {
        return getMessages('sent-in-last-minute');
      },
      selector: 'error-sent-in-last-minute',
    },

    getSmsDayLimit: {
      code: DAY_LIMIT_GET_SMS,
      get messages() {
        return getMessages('get-sms-day-limit');
      },
      selector: 'error-get-sms-day-limit',
    },

    expiredDateValidation: {
      code: EXPIRED_DATE_FIELD_VALIDATION_FAILED,
      get messages() {
        return getMessages('expired-date-validation');
      },
      selector: 'error-expired-date-validation',
    },

    validateAge: {
      rule({ value }) {
        const validationAgeValue = 18;
        const parsedYear = Number(value.split('.')[2]);
        const parsedMonth = Number(value.split('.')[1]);
        const parsedDay = Number(value.split('.')[0]);
        const age = currentDate().year - parsedYear;

        const isAdult =
          age > validationAgeValue ||
          (age === validationAgeValue && currentDate().month > parsedMonth) ||
          (age === validationAgeValue &&
            currentDate().month === parsedMonth &&
            currentDate().day >= parsedDay);
        return !isAdult;
      },
      get messages() {
        return getMessages('validate-age');
      },
      selector: 'error-validate-age',
    },

    birthPlaceMaxLength: {
      rule({ value }) {
        return value.length > BIRTH_PLACE_TEXT_MAX_LENGTH;
      },
      get messages() {
        return getMessages('birth-place-max-length');
      },
      selector: 'error-birth-place-max-length',
    },

    validateExpense: {
      rule({ secondValue, value }) {
        return parseInt(value, 10) >= parseInt(secondValue, 10);
      },
      get messages() {
        return getMessages('validate-expense');
      },
      selector: 'error-validate-expense',
    },

    walletIsNotIdentified: {
      code: WALLET_IS_NOT_IDENTIFIED,
      get messages() {
        return getMessages('wallet-is-not-identified');
      },
      selector: 'error-wallet-is-not-identified',
    },

    invalidMobileOperator: {
      rule({ value }) {
        return !app.store.getters[WALLETS_STORE_GETTERS.IS_PHONE_HAVE_VALID_OPERATOR_CODE](value);
      },
      get messages() {
        return getMessages('invalid-mobile-operator');
      },
      selector: 'error-invalid-mobile-operator',
    },

    notUnique: {
      code: NOT_UNIQUE,
      get messages() {
        return getMessages('not-unique');
      },
      selector: 'error-not-unique',
    },

    invalidPhoneFormat: {
      code: INVALID_PHONE_FORMAT,
      get messages() {
        return getMessages('invalid-phone-format');
      },
      selector: 'error-invalid-phone-format',
    },

    incorrectWalletPhoneNumber: {
      code: NOT_SAME_AS_CLIENT_PHONE,
      get messages() {
        return getMessages('incorrect-wallet-phone-number');
      },
      selector: 'error-incorrect-phone-number',
    },

    invalidRecaptcha: {
      code: INVALID_RECAPTCHA_TOKEN,
      get messages() {
        return getMessages('invalid-recaptcha');
      },
      selector: 'error-invalid-recaptcha',
    },
  });
};
